import type { User, UserManager } from 'oidc-client-ts';

const SILENT_SIGNIN_TIMEOUT_MS = 10000;

let recovery_promise: Promise<User | null> | null = null;

export function isSessionRecoveryInProgress(): boolean {
  return recovery_promise !== null;
}

export function signinSilentWithTimeout(
  user_manager: UserManager,
): Promise<User | null> {
  return new Promise((resolve, reject) => {
    const timeout_id = window.setTimeout(() => {
      reject(new Error('Silent sign-in timed out'));
    }, SILENT_SIGNIN_TIMEOUT_MS);

    user_manager
      .signinSilent({ silentRequestTimeoutInSeconds: 10 })
      .then((user) => {
        window.clearTimeout(timeout_id);
        resolve(user);
      })
      .catch((error: unknown) => {
        window.clearTimeout(timeout_id);
        reject(error);
      });
  });
}

export function clearStoredSession(user_manager: UserManager | null): void {
  localStorage.removeItem('accqsure_jwt');
  if (!user_manager) return;
  void user_manager.removeUser().catch((error: unknown) => {
    console.log('auth remove user failed', error);
  });
}

export async function startInteractiveLogin(
  user_manager: UserManager,
): Promise<void> {
  clearStoredSession(user_manager);
  try {
    await user_manager.signinRedirect();
  } catch (error) {
    console.log('signin redirect failed', error);
  }
}

async function attemptRecovery(user_manager: UserManager): Promise<User | null> {
  try {
    const refreshed_user = await signinSilentWithTimeout(user_manager);
    if (refreshed_user?.access_token && !refreshed_user.expired) {
      return refreshed_user;
    }
  } catch (error) {
    console.log('Silent sign-in failed:', error);
  }
  await startInteractiveLogin(user_manager);
  return null;
}

export function recoverExpiredSession(
  user_manager: UserManager,
): Promise<User | null> {
  if (!recovery_promise) {
    recovery_promise = attemptRecovery(user_manager).finally(() => {
      recovery_promise = null;
    });
  }
  return recovery_promise;
}
